// ANGULAR
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { EdicionService } from '@api-requests-services/edicion.service';
// MODELS
import { PostStartEditingRequest } from '@models/edicion.model';
// COMPONENTS
import { M2mComponent } from '@features/home/options/m2m/m2m.component';
import { IpsComponent } from '@features/home/options/ips/ips.component';
import { GruposComponent } from '@features/home/options/grupos/grupos.component';
import { TokensComponent } from '@features/home/options/tokens/tokens.component';
import { EdicionComponent } from '@features/home/options/edicion/edicion.component';
import { BackupsComponent } from '@features/home/options/backups/backups.component';
import { SecretosComponent } from '@features/home/options/secretos/secretos.component';
import { AplicacionesComponent } from '@features/home/options/aplicaciones/aplicaciones.component';
import { ActualizacionesComponent } from '@features/home/options/actualizaciones/actualizaciones.component';
import { ConfiguracionesComponent } from '@features/home/options/configuraciones/configuraciones.component';
import { ConnectionStringsComponent } from '@features/home/options/connection-strings/connection-strings.component';
// STORE
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { MainState } from '@store/main.reducer';
import { logout } from '@store/session/session.actions';
import { updateReturnHome, updateShowClientsRequests } from '@store/app/app.actions';
// OTHERS
import Swal from 'sweetalert2';
import { Paths } from '@constants/paths.const';

enum Options {
  Tokens = 'tokens',
  M2M = 'm2m',
  IPs = 'ips',
  Grupos = 'grupos',
  Edicion = 'edicion',
  ConnectionStrings = 'connectionStrings',
  Configurations = 'configurations',
  Backups = 'backups',
  Aplicaciones = 'aplicaciones',
  Actualizaciones = 'actualizaciones',
  Secretos = 'secretos'
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  imports: [
    CommonModule,
    FormsModule,
    TokensComponent,
    M2mComponent,
    IpsComponent,
    GruposComponent,
    EdicionComponent,
    ConnectionStringsComponent,
    ConfiguracionesComponent,
    BackupsComponent,
    AplicacionesComponent,
    ActualizacionesComponent,
    SecretosComponent,
  ],
  providers: [DatePipe]
})
export class HomeComponent implements OnInit {
  options = Options;
  currentOption: string;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private edicionService: EdicionService,
    private store: Store<MainState>,
    private router: Router,
  ) {
    this.currentOption = this.options.Edicion;
  }

  ngOnInit(): void {
    this.getModoEdicion();
  }

  getModoEdicion(): void {
    this.edicionService.getModoEdicion().subscribe(
      editMode => {
        if (editMode) {
          // console.log('Modo edición: ', editMode); 

          if (editMode.modoEdicion) {
            this.globalService.setEditing(true);
            this.globalService.setPressStartEditingButton(true);

            this.alertService.showBasicMessage(
              `¡Atención! El Modo Edición ya está iniciado`, 
              `Comentario: ${editMode.comentario ?? ''}`, 
              "warning", 
              true
            );
          } else {
            this.globalService.setEditing(false);
            this.globalService.setPressStartEditingButton(false);
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Edit Mode:', error);
        }
      }
    );
  }

  startEditing(): void {
    this.alertService.showInputAlert(
      "Iniciar edición",
      "Escribe comentario informativo de inicio de edición.",
      "Iniciar edición"
    ).then((result) => {
      if (result.isConfirmed) {
        const postStartEditingRequest: PostStartEditingRequest = { comentario: !!result.value && result.value.trim() !== '' ? result.value : undefined };

        this.edicionService.postStartEditing(postStartEditingRequest).subscribe(
          () => {
            // console.log('Edición iniciada');
            this.globalService.setEditing(true);
          },
          error => {
            if (error.status === 409) {
              this.globalService.setEditing(true);

              this.alertService.showBasicMessage(
                `¡Atención! El Modo Edición ya está iniciado`, 
                `Comentario: ${error.error}`, 
                "warning", 
                true
              );
            } else {
              this.alertService.swalError(error.error);
              console.error('Error Start Editing:', error);
            }
          }
        );
      }
    });
  }

  finishEditing(): void {
    this.edicionService.getModoEdicion().subscribe(
      editMode => {
        if (editMode) {
          // console.log('Modo edición: ', editMode); 

          if (editMode.modoEdicion) {
            this.globalService.setEditing(true);
            this.globalService.setPressStartEditingButton(true);
            this.edicionService.getEditado().subscribe(
              edited => {
                if (edited !== undefined && edited === false) {
                  this.processFinishEditing(false);
                } else if (edited !== undefined && edited === true) {
                  // Especial: showConfirmAlert con 3 botones
                  Swal.fire({
                    icon: "warning",
                    title: "¿Quieres guardar los cambios?",
                    showConfirmButton: true,
                    confirmButtonColor: 'var(--primary-color)',
                    confirmButtonText: "Guardar cambios",
                    showDenyButton: true,
                    denyButtonColor: 'var(--false-color)',
                    denyButtonText: "No guardar cambios",
                    showCancelButton: true,
                    cancelButtonText: "Cancelar",
                    reverseButtons: true
                  }).then((result) => {
                    if (result.isConfirmed) {
                      this.processFinishEditing(true);
                    } else if (result.isDenied) {
                      this.processFinishEditing(false);
                    }
                  });
                }
              },
              error => {
                this.alertService.swalError(error.error);
                console.error('Error getting Edited:', error);
              }
            );
          } else {
            this.globalService.setEditing(false);
            this.globalService.setPressStartEditingButton(false);
            this.alertService.showBasicMessage(`El oráculo no estaba en modo edición`, ``, "warning", true);
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Edit Mode:', error);
        }
      }
    );
  }

  finishBackupPreview(): void {
    this.globalService.setBackupToPreview(null);
    this.currentOption = this.options.Edicion;
  }

  processFinishEditing(aceptarCambios: boolean): void {
    this.edicionService.postFinishEditing(aceptarCambios).subscribe(
      () => {
        // console.log('Edición finalizada');
        this.globalService.setEditing(false);
        this.globalService.setPressStartEditingButton(false);
        this.currentOption = this.options.Edicion;

        if (aceptarCambios) {
          this.alertService.showAutoCloseMessage(`¡Cambios guardados!`, ``, "success", 1200);
        }
      },
      error => {
        this.alertService.swalError(error.error);
        console.error('Error Finish Editing:', error);
      }
    );
  }

  goToPasswordChangeRequired(): void {
    this.store.dispatch(updateReturnHome({ returnHome: true }));
    this.router.navigate([Paths.changePasswordRequired]);
  }

  showClientsRequests(): void {
    this.store.dispatch(updateShowClientsRequests({ showClientsRequests: true }));
    if (this.currentOption !== this.options.Actualizaciones) { this.currentOption = this.options.Actualizaciones; }
  }

  logout(): void {
    this.store.dispatch(logout());
  }
}